<template>
  <div>
    <a-card :title="$t('label.workspace.configuration')">
      <a-form layout="vertical">
        <a-form-item v-if="isDomainAdmin" :label="$t('label.workspace.configuration.inherit')">
          <a-switch v-model:checked="configurationInherit" :disabled="!canUpdateConfiguration" />
        </a-form-item>
        <a-form-item :label="$t('label.workspace.minimum.disk.size')">
          <a-input-number
            v-model:value="minimumDiskSize"
            :min="1"
            :precision="0"
            :disabled="configurationInherit || !canUpdateConfiguration"
            style="width: 100%" />
          <div class="form-help">{{ $t('message.workspace.minimum.disk.size') }}</div>
        </a-form-item>
        <a-button type="primary" :loading="configurationSaving" :disabled="!canUpdateConfiguration" @click="saveConfiguration">
          {{ $t('label.save') }}
        </a-button>
      </a-form>
    </a-card>
    <a-card :title="$t('label.workspace.managed.scripts')" style="margin-top: 16px">
      <a-alert
        type="info"
        show-icon
        style="margin-bottom: 16px"
        :message="$t('message.workspace.managed.scripts')" />
      <a-form layout="vertical">
        <a-form-item v-if="isDomainAdmin" :label="$t('label.workspace.managed.inherit')">
          <a-switch v-model:checked="inherit" :disabled="!canUpdate" />
          <div class="form-help">{{ $t('message.workspace.managed.inherit') }}</div>
        </a-form-item>
        <a-form-item :label="$t('label.workspace.managed.enabled')">
          <a-switch v-model:checked="managed" :disabled="inherit || !canUpdate" />
        </a-form-item>
        <a-form-item :label="$t('label.workspace.windows.powershell.script')">
          <a-textarea v-model:value="windows" :rows="12" :disabled="inherit || !managed || !canUpdate" />
          <div class="form-help">{{ $t('message.workspace.windows.powershell.script') }}</div>
        </a-form-item>
        <a-form-item :label="$t('label.workspace.linux.shell.script')">
          <a-textarea v-model:value="linux" :rows="12" :disabled="inherit || !managed || !canUpdate" />
          <div class="form-help">{{ $t('message.workspace.linux.shell.script') }}</div>
        </a-form-item>
        <a-button type="primary" :loading="saving" :disabled="!canUpdate" @click="save">
          {{ $t('label.save') }}
        </a-button>
      </a-form>
    </a-card>
  </div>
</template>

<script>
import { getAPI, postAPI } from '@/api'

export default {
  name: 'WorkspaceManagedScripts',
  props: {
    resource: { type: Object, required: false, default: null }
  },
  data () {
    return {
      configured: false,
      inherit: false,
      managed: false,
      windows: '',
      linux: '',
      saving: false,
      configurationConfigured: false,
      configurationInherit: false,
      minimumDiskSize: 100,
      configurationSaving: false
    }
  },
  computed: {
    canUpdate () { return 'updateWorkspaceManagedScripts' in this.$store.getters.apis },
    canUpdateConfiguration () { return 'updateWorkspaceConfiguration' in this.$store.getters.apis },
    domainId () { return this.resource?.id },
    isDomainAdmin () { return this.$store.getters.userInfo.roletype === 'DomainAdmin' }
  },
  created () {
    this.load()
    this.loadConfiguration()
  },
  watch: {
    domainId () {
      this.load()
      this.loadConfiguration()
    }
  },
  methods: {
    params () { return this.domainId ? { domainid: this.domainId } : {} },
    load () {
      getAPI('listWorkspaceManagedScripts', this.params()).then(json => {
        const response = json.workspacemanagedscriptsresponse
        const result = response.payload || response
        this.configured = Boolean(result.configured)
        this.inherit = this.isDomainAdmin && !this.configured
        this.managed = result.managed
        this.windows = result.windows || ''
        this.linux = result.linux || ''
      })
    },
    loadConfiguration () {
      getAPI('listWorkspaceConfigurations', this.params()).then(json => {
        const response = json.workspaceconfigurationsettingsresponse
        const result = response.payload || response
        this.configurationConfigured = Boolean(result.configured)
        this.configurationInherit = this.isDomainAdmin && !this.configurationConfigured
        this.minimumDiskSize = result.minimumdisksize || 100
      })
    },
    save () {
      this.saving = true
      postAPI('updateWorkspaceManagedScripts', {
        ...this.params(), inherit: this.inherit, managed: this.managed, windows: this.windows, linux: this.linux
      }).then(() => {
        this.$notification.success({ message: this.$t('label.success'), description: this.$t('message.workspace.managed.scripts.saved') })
        this.load()
      }).catch(error => {
        this.$notification.error({ message: this.$t('label.error'), description: error?.response?.data?.workspacemanagedscriptsresponse?.errortext || error.message })
      }).finally(() => { this.saving = false })
    },
    saveConfiguration () {
      this.configurationSaving = true
      postAPI('updateWorkspaceConfiguration', {
        ...this.params(),
        inherit: this.configurationInherit,
        minimum_disk_size: this.configurationInherit ? undefined : this.minimumDiskSize
      }).then(() => {
        this.$notification.success({ message: this.$t('label.success'), description: this.$t('message.workspace.configuration.saved') })
        this.loadConfiguration()
      }).catch(error => {
        this.$notification.error({ message: this.$t('label.error'), description: error?.response?.data?.workspaceconfigurationsettingsresponse?.errortext || error.message })
      }).finally(() => { this.configurationSaving = false })
    }
  }
}
</script>

<style scoped>
.form-help {
  color: rgba(0, 0, 0, 0.55);
  font-size: 12px;
  margin-top: 4px;
}
</style>
